import { useState } from "react";
import {
    useContractRead,
    useContractWrite,
    useWalletClient,
} from "wagmi";
import { waitForTransaction } from "wagmi/actions";
import { getContractMethod } from "../ethers/contracts";
import { isEnabled } from "../ethers/tokens";
import {
    APPROVE_AND_MINT_FUNCTION_NAME,
    APPROVE_FUNCTION_NAME,
    CONFIRMATIONS_COUNT,
    ERROR_WISELENDING_APPROVAL_APPROVE,
    ERROR_WISELENDING_APPROVAL_FETCH,
    ERROR_WISELENDING_APPROVAL_WRITE,
    INFLATE_PERCENT
} from "../utils/config";
import { useGasEstimate } from "./useGasEstimate";
// import { parseUnits } from "viem";

export const useTokenApproval = ({
    config,
    nftId,
    token,
    amount
}) => {

    const { data: signer } = useWalletClient();
    const {
        chainId,
        version,
        positionNFTContract,
        aaveHubContract
    } = config;

    const enabled = isEnabled({
        config,
        nftId,
        token
    }) && token.isAavePool;

    const [isApproved, setIsApproved] = useState(false);
    const [isFetching, setIsFetching] = useState(enabled);

    const approvalMethod = getContractMethod({
        name: "aaveTokenApproval",
        chainId,
        version,
    }) ?? "getApprovedSoft";

    const functionName = enabled
        ? approvalMethod
        : null;

    useContractRead({
        chainId,
        address: positionNFTContract?.address,
        abi: positionNFTContract?.abi,
        signerOrProvider: signer,
        functionName,
        args: [
            nftId
        ],
        watch: true,
        enabled,
        onError: (error) => {
            console.error(
                ERROR_WISELENDING_APPROVAL_FETCH,
                error
            );
        },
        onSuccess: (data) => {
            setIsApproved(
                data === aaveHubContract?.address
            );
            setIsFetching(false);
        }
    });

    const approveArgs = [
        aaveHubContract?.address,
        nftId,
    ];

    let functionNameToExecute = APPROVE_FUNCTION_NAME;

    if (token.isAavePool && isApproved === false) {
        functionNameToExecute = APPROVE_AND_MINT_FUNCTION_NAME;
    }

    const {
        gasLimit,
        estimatedGas,
        estimatesGasFormatted,
        estimatedGasUSD,
        isError
    } = useGasEstimate({
        contract: positionNFTContract,
        // functionName: APPROVE_FUNCTION_NAME,
        functionName: functionNameToExecute,
        args: approveArgs,
        account: signer?.account,
        amount
    });

    let gasLimitFormatted = parseInt(
        parseInt(gasLimit) * INFLATE_PERCENT
    ).toString();

    const tokenContract = useContractWrite({
        chainId,
        address: positionNFTContract?.address,
        abi: positionNFTContract?.abi,
        signerOrProvider: signer,
        // functionName: APPROVE_FUNCTION_NAME,
        functionName: functionNameToExecute,
        args: approveArgs,
        gas: gasLimitFormatted,
        enabled,
        onError: (error) => {
            console.error(
                ERROR_WISELENDING_APPROVAL_WRITE,
                error
            );
        }
    });

    const approve = async ({
        onConfirmation,
        onApprovalComplete,
        onApproving,
        onError
    }) => {
        try {

            onConfirmation?.();

            const approveTransaction = await tokenContract.writeAsync();
            const { hash } = approveTransaction;

            onApproving?.(
                hash
            );

            const approve = await waitForTransaction({
                hash,
                confirmations: CONFIRMATIONS_COUNT
            });

            setIsApproved(
                true
            );

            onApprovalComplete?.(
                approve
            );

            return true;

        } catch (error) {

            console.error(
                ERROR_WISELENDING_APPROVAL_APPROVE,
                error
            );

            onError?.(error);
            return false;
        }
    };

    return {
        approve,
        isApproved,
        isFetching,
        estimatedGas,
        estimatesGasFormatted,
        estimatedGasUSD,
        estimateHasError: isError
    };
};
